import type { ReactNode } from 'react';
import type { RecoilValueReadOnly } from 'recoil';
import type { VSGAGutachten, VSGAGutachtenComplete } from '@zurich/api/legacy';
import type {
  MultipleValidationResults,
  UiOption,
  ValidationResult,
} from '@zurich/common/types';
import type { IconVariant } from '@zurich/ui/atoms';

export type VSGAStep = {
  stepName: StepNames;
  title: string;
  subtitle?: string;
  Component: () => ReactNode;
  nextButtonText: string;
  validationSelector?: RecoilValueReadOnly<
    ValidationResult | MultipleValidationResults
  >;
  renderNext?: boolean;
  disabled?: boolean;
  disabledNext?: boolean;
  disabledNextReason?: string;
  onNext?: () => Promise<void>;
  nextButtonIcon?: IconVariant;
}[];

interface DocumentPhoto {
  documentId?: string;
  fileId?: string;
}

type Damages = NonNullable<VSGAGutachten['damages']>;

export type Damage = Damages[number] &
  DocumentPhoto & {
    typeOfDamage?: string;
    descriptionOfDamage?: string;
    areaOfDamage?: string;
  };

type Photos = NonNullable<VSGAGutachten['photos']>;

export type Photo = Photos[number] &
  DocumentPhoto & {
    imageURL?: string;
  };

/**
 * `VSGAPayload` is used to track all state within the
 * VSGA component, but only some fields are sent to the
 * LegAdd component.
 *
 * All fields are stored in Recoil selectors
 * and saved to local storage so the user sees all of the
 * fields they have entered.
 */
export interface VSGAPayload extends VSGAGutachtenComplete {}

export type AutoArea =
  | 'front'
  | 'rear'
  | 'windshield'
  | 'passengerside'
  | 'driverside'
  | 'roofLeft'
  | 'roofRight'
  | 'frontPassengersideAngle'
  | 'frontDriversideAngle'
  | 'backPassengersideAngle'
  | 'backDriversideAngle';

export type AutoInfo = 'kilometers' | 'serialnumber';

export type AutoPhotoSteps = AutoArea | AutoInfo;

/**
 * `isPhotoRequired` is used to check if a step requires a photo to be taken.
 * @param input
 * @returns boolean
 */
export function isPhotoRequired(input: string): input is AutoPhotoSteps {
  switch (input) {
    case 'front':
    case 'rear':
    case 'windshield':
    case 'passengerside':
    case 'driverside':
    case 'kilometers':
    case 'serialnumber':
    case 'roofLeft':
    case 'roofRight':
    case 'frontPassengersideAngle':
    case 'frontDriversideAngle':
    case 'backPassengersideAngle':
    case 'backDriversideAngle':
      return true;
    default:
      return false;
  }
}

export function isAutoArea(input: string): input is AutoArea {
  switch (input) {
    case 'front':
    case 'rear':
    case 'windshield':
    case 'passengerside':
    case 'driverside':
    case 'roofLeft':
    case 'roofRight':
    case 'frontPassengersideAngle':
    case 'frontDriversideAngle':
    case 'backPassengersideAngle':
    case 'backDriversideAngle':
      return true;
    default:
      return false;
  }
}

export const stepNames = [
  'welcome',
  'contact',
  'introduction',
  'preCheck',
  'licensePlate',
  'autoSummary',
  'photoSummary',
  'documentDamage',
  'photosOverview',
] as const;
export type StepNames = (typeof stepNames)[number] | AutoPhotoSteps;

export type SummaryConfigType = {
  titleTranslationKey: string;
  numberOfDamages: number;
  onClickParam: StepNames;
};

export type SummaryDataType = {
  title: string;
  info: string;
  onClick: () => void;
};

export const vehiclePartOptions: Record<AutoArea, Array<UiOption<string>>> = {
  front: [
    {
      label: 'Vordere Stoßstange',
      value: 'Vordere Stoßstange',
    },
    {
      label: 'Kühlergrill',
      value: 'Kühlergrill',
    },
    {
      label: 'Motorhaube',
      value: 'Motorhaube',
    },
    {
      label: 'Sonstiges',
      value: 'Sonstiges',
    },
  ],
  rear: [
    {
      label: 'Stoßstange',
      value: 'Stoßstange',
    },
    {
      label: 'Rückleuchte',
      value: 'Rückleuchte',
    },
    {
      label: 'Heckscheibe',
      value: 'Heckscheibe',
    },
    {
      label: 'Kofferraumdeckel',
      value: 'Kofferraumdeckel',
    },
    {
      label: 'Reifen/Felgen',
      value: 'Reifen/Felgen',
    },
  ],
  windshield: [
    {
      label: 'Windschutzscheibe',
      value: 'Windschutzscheibe',
    },
  ],
  passengerside: [
    {
      label: 'Türe vorne',
      value: 'Türe vorne',
    },
    {
      label: 'Kotflügel vorne',
      value: 'Kotflügel vorne',
    },
    {
      label: 'Türe hinten',
      value: 'Türe hinten',
    },
    {
      label: 'Seitenteil hinten',
      value: 'Seitenteil hinten',
    },
    {
      label: 'Seitenscheibe',
      value: 'Seitenscheibe',
    },
    {
      label: 'Reifen/Felgen',
      value: 'Reifen/Felgen',
    },
  ],
  driverside: [
    {
      label: 'Türe vorne',
      value: 'Türe vorne',
    },
    {
      label: 'Kotflügel vorne',
      value: 'Kotflügel vorne',
    },
    {
      label: 'Türe hinten',
      value: 'Türe hinten',
    },
    {
      label: 'Seitenteil hinten',
      value: 'Seitenteil hinten',
    },
    {
      label: 'Seitenscheibe',
      value: 'Seitenscheibe',
    },
    {
      label: 'Reifen/Felgen',
      value: 'Reifen/Felgen',
    },
  ],
  roofLeft: [
    {
      label: 'Dach',
      value: 'Dach',
    },
  ],
  roofRight: [
    {
      label: 'Dach',
      value: 'Dach',
    },
  ],
  backDriversideAngle: [
    {
      label: 'Türe hinten',
      value: 'Türe hinten',
    },
    {
      label: 'Kotflügel hinten',
      value: 'Kotflügel hinten',
    },
    {
      label: 'Rückleuchte',
      value: 'Rückleuchte',
    },
    {
      label: 'Seitenscheibe',
      value: 'Seitenscheibe',
    },
  ],
  frontDriversideAngle: [
    {
      label: 'Türe vorne',
      value: 'Türe vorne',
    },
    {
      label: 'Kotflügel vorne',
      value: 'Kotflügel vorne',
    },
    {
      label: 'Scheinwerfer',
      value: 'Scheinwerfer',
    },
    {
      label: 'Seitenspiegel',
      value: 'Seitenspiegel',
    },
  ],
  frontPassengersideAngle: [
    {
      label: 'Türe vorne',
      value: 'Türe vorne',
    },
    {
      label: 'Kotflügel vorne',
      value: 'Kotflügel vorne',
    },
    {
      label: 'Scheinwerfer',
      value: 'Scheinwerfer',
    },
    {
      label: 'Seitenscheibe',
      value: 'Seitenscheibe',
    },
  ],
  backPassengersideAngle: [
    {
      label: 'Türe hinten',
      value: 'Türe hinten',
    },
    {
      label: 'Kotflügel hinten',
      value: 'Kotflügel hinten',
    },
    {
      label: 'Rückleuchte',
      value: 'Rückleuchte',
    },
    {
      label: 'Seitenscheibe',
      value: 'Seitenscheibe',
    },
  ],
};
